'use client'

import Image from 'next/image'
import Link from 'next/link'
import { cloneElement, isValidElement, useEffect, useRef, useState, type ReactElement, type ReactNode, type TouchEvent } from 'react'
import { resolveHomepageSearchDescription, resolveHomepageSearchTitle } from '@/lib/homepage-search-title'
import { resolveHomepageSearchMode } from '@/lib/homepage-search-mode'

type MediaValue = {
  alt?: string | null
  url?: string | null
  sizes?: {
    heroDesktop?: { url?: string | null } | null
    heroMobile?: { url?: string | null } | null
  }
} | string | null

type HomepageSlide = {
  tabLabel: string
  searchMode?: 'purchase' | 'rent-to-buy' | 'sell' | 'commercial' | null
  title: string
  description?: string | null
  searchTitle?: string | null
  searchDescription?: string | null
  primaryActionLabel?: string | null
  primaryActionUrl?: string | null
  secondaryActionLabel?: string | null
  secondaryActionUrl?: string | null
  desktopImage: MediaValue | undefined
  mobileImage?: MediaValue
}

export type HomepageServiceCard = {
  title: string
  description: string
  href: string
  image?: MediaValue
  showOnDesktop?: boolean | null
  showOnMobile?: boolean | null
}

export type HomepageOffersSection = {
  autoplaySeconds?: number | null
  eyebrow?: string | null
  title?: string | null
  descriptionDesktop?: string | null
  descriptionMobile?: string | null
  allOffersLabel?: string | null
  allOffersMobileLabel?: string | null
  allOffersHref?: string | null
  badgeLabel?: string | null
  readyLabel?: string | null
  priceLabel?: string | null
  monthlyPrefix?: string | null
  lockPriceLabel?: string | null
}

export type HomepageRentToBuyOption = {
  label: string
  icon: 'swap' | 'return' | 'key'
}

export type HomepageRentToBuySection = {
  heroImage?: MediaValue
  badge?: string | null
  title?: string | null
  description?: string | null
  options?: HomepageRentToBuyOption[] | null
  benefit?: string | null
  monthlyPrice?: string | null
  monthlySuffix?: string | null
  priceNote?: string | null
  primaryLabel?: string | null
  primaryHref?: string | null
  chatLabel?: string | null
  chatHref?: string | null
  howItWorksLabel?: string | null
  howItWorksHref?: string | null
  inventoryTitle?: string | null
  inventorySummary?: string | null
  inventorySummaryMobile?: string | null
  inventoryAdvance?: string | null
  cardBadge?: string | null
  cardButtonLabel?: string | null
  inventoryAllLabel?: string | null
  inventoryAllHref?: string | null
}

export type HomepageCommercialVehiclesSection = {
  desktopImage?: MediaValue
  mobileImage?: MediaValue
  eyebrow?: string | null
  title?: string | null
  description?: string | null
  catalogTitle?: string | null
  primaryLabel?: string | null
  primaryHref?: string | null
  contactLabel?: string | null
  contactHref?: string | null
  vatLabel?: string | null
  vehicleButtonLabel?: string | null
  catalogHref?: string | null
}

export type HomepageSellCtaSection = {
  desktopImage?: MediaValue
  mobileImage?: MediaValue
  eyebrow?: string | null
  title?: string | null
  descriptionDesktop?: string | null
  descriptionMobile?: string | null
  primaryLabel?: string | null
  primaryHref?: string | null
  contactLabel?: string | null
  contactHref?: string | null
}

export type HomepageReview = {
  id?: string | null
  author: string
  reviewText: string
  rating?: number | null
}

export type HomepageReviewsSection = {
  enabled?: boolean | null
  eyebrow?: string | null
  title?: string | null
  score?: string | null
  scoreCaption?: string | null
  allReviewsLabel?: string | null
  allReviewsHref?: string | null
  reviews?: HomepageReview[] | null
}

export type HomepageLocationCard = {
  id?: string | null
  title: string
  address: string
  phone?: string | null
  image: MediaValue
  slug?: string | null
  mapsUrl?: string | null
}

export type HomepageLocationsSection = {
  enabled?: boolean | null
  title?: string | null
  headline?: string | null
  buttonLabel?: string | null
  cards?: HomepageLocationCard[] | null
}

export type HomepageContent = {
  slides?: HomepageSlide[] | null
  heroAutoplaySeconds?: number | null
  searchPanel?: {
    title?: string | null
    description?: string | null
  } | null
  servicesSection?: {
    autoplaySeconds?: number | null
    eyebrow?: string | null
    title?: string | null
    cards?: HomepageServiceCard[] | null
  } | null
  promoSection?: {
    desktopImage?: MediaValue
    mobileImage?: MediaValue
    href?: string | null
    linkLabel?: string | null
  } | null
  offersSection?: HomepageOffersSection | null
  rentToBuySection?: HomepageRentToBuySection | null
  commercialVehiclesSection?: HomepageCommercialVehiclesSection | null
  sellCtaSection?: HomepageSellCtaSection | null
  reviewsSection?: HomepageReviewsSection | null
  locationsSection?: HomepageLocationsSection | null
}

export function getHomepageMediaUrl(media: MediaValue | undefined, size: 'heroDesktop' | 'heroMobile' | 'original' = 'heroDesktop') {
  if (typeof media === 'string') return media
  if (size === 'original') return media?.url || null
  return media?.sizes?.[size]?.url || media?.url || null
}

export function getHomepageMediaAlt(media: MediaValue | undefined) {
  return typeof media === 'object' && media ? media.alt || '' : ''
}

export default function PayloadHomepageHero({
  content,
  searchPanel,
  activeIndex,
  onActiveIndexChange,
}: {
  content: HomepageContent
  searchPanel?: ReactNode
  activeIndex: number
  onActiveIndexChange: (index: number) => void
}) {
  const slides = content.slides || []
  const activeSlide = slides[activeIndex]
  const [autoplayActive, setAutoplayActive] = useState(true)
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const autoplaySeconds = Math.max(0, Number(content.heroAutoplaySeconds ?? 30))

  const handleTouchStart = (event: TouchEvent<HTMLElement>) => {
    if (event.touches.length !== 1) {
      touchStart.current = null
      return
    }
    touchStart.current = { x: event.touches[0].clientX, y: event.touches[0].clientY }
  }

  const handleTouchEnd = (event: TouchEvent<HTMLElement>) => {
    const start = touchStart.current
    touchStart.current = null
    if (!start || slides.length < 2 || (event.target as HTMLElement).closest('button, a, input, select, textarea')) return

    const touch = event.changedTouches[0]
    const deltaX = touch.clientX - start.x
    const deltaY = touch.clientY - start.y
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return

    setAutoplayActive(false)
    onActiveIndexChange((activeIndex + (deltaX < 0 ? 1 : -1) + slides.length) % slides.length)
  }

  useEffect(() => {
    if (!autoplayActive || autoplaySeconds <= 0 || slides.length < 2) return

    const timerId = window.setInterval(() => {
      onActiveIndexChange((activeIndex + 1) % slides.length)
    }, autoplaySeconds * 1000)

    return () => window.clearInterval(timerId)
  }, [activeIndex, autoplayActive, autoplaySeconds, onActiveIndexChange, slides.length])

  if (!activeSlide) return null

  const desktopImage = getHomepageMediaUrl(activeSlide.desktopImage)
  const mobileImage = getHomepageMediaUrl(activeSlide.mobileImage, 'heroMobile') || desktopImage
  const alt = getHomepageMediaAlt(activeSlide.mobileImage) || getHomepageMediaAlt(activeSlide.desktopImage)
  const searchTitle = resolveHomepageSearchTitle(activeSlide.searchTitle, content.searchPanel?.title)
  const searchDescription = resolveHomepageSearchDescription(activeSlide.searchDescription, content.searchPanel?.description)
  const searchMode = resolveHomepageSearchMode(activeSlide.searchMode, activeSlide.tabLabel, activeIndex)

  return (
    <section data-home-hero onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} className={`relative z-10 isolate ${searchMode === 'purchase' ? 'h-[680px]' : 'h-[636px]'} overflow-visible bg-white text-white lg:h-[692px]`}>
      <div className="absolute inset-x-0 top-0 z-0 h-[486px] overflow-hidden bg-[#142c50] lg:inset-0 lg:h-auto">
      <div className="absolute inset-0 bg-gradient-to-r from-[#0d1e3a] via-[#163b68]/70 to-transparent" />
      {desktopImage && (
        <Image
          src={desktopImage}
          alt={alt}
          fill
          priority={activeIndex === 0}
          unoptimized
          sizes="100vw"
          className="hidden object-cover sm:block"
        />
      )}
      {mobileImage && (
        <div className="absolute inset-x-0 bottom-0 top-auto h-[318px] overflow-hidden sm:hidden">
          <Image
            src={mobileImage}
            alt={alt}
            fill
            priority={activeIndex === 0}
            unoptimized
            sizes="100vw"
            className="object-cover object-bottom"
          />
        </div>
      )}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(16,35,66,0.98)_0%,rgba(16,35,66,0.92)_38%,rgba(16,35,66,0)_68%)] lg:hidden" />
      <div className="absolute inset-0 hidden lg:block" style={{ backgroundImage: 'linear-gradient(180deg, rgba(8, 20, 43, 0.55) 0%, rgba(8, 20, 43, 0) 30%), linear-gradient(90deg, rgba(8, 20, 43, 0.92) 0%, rgba(8, 20, 43, 0.62) 42%, rgba(8, 20, 43, 0) 72%)' }} />
      </div>

      <div className="homepage-content-container relative z-10 h-full pt-5 lg:flex lg:flex-col lg:pb-0 lg:pt-[52px]">
        <div className="max-w-[620px] lg:max-w-[650px]">
          <h1 className="text-[34px] font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-[60px] lg:font-extrabold lg:leading-[1.02] lg:tracking-[-1.8px]">
            {activeSlide.title}
          </h1>
          {activeSlide.description && (
            <p className="mt-3 max-w-[560px] text-[15px] leading-[1.45] text-white/85 sm:text-lg lg:mt-5 lg:w-[520px] lg:text-[19px] lg:leading-[1.5]">
              {activeSlide.description}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2.5 lg:mt-5 lg:gap-[14px]">
            {activeSlide.primaryActionLabel && activeSlide.primaryActionUrl && (
              <Link
                href={activeSlide.primaryActionUrl}
                className="inline-flex min-h-[42px] flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-[10px] bg-white px-3 text-[13px] font-semibold text-[#102342] transition-colors hover:bg-slate-100 sm:flex-none sm:px-6 sm:text-base lg:min-h-14 lg:gap-3 lg:rounded-[14px] lg:px-[26px]"
              >
                {activeSlide.primaryActionLabel}
                <span aria-hidden="true">→</span>
              </Link>
            )}
            {activeSlide.secondaryActionLabel && activeSlide.secondaryActionUrl && (
              <Link
                href={activeSlide.secondaryActionUrl}
                className="inline-flex min-h-[42px] flex-1 items-center justify-center whitespace-nowrap rounded-[10px] border border-white/70 bg-white/10 px-3 text-[13px] font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:flex-none sm:px-6 sm:text-base lg:min-h-14 lg:rounded-[14px] lg:border-[1.5px] lg:border-white/45 lg:px-6"
              >
                {activeSlide.secondaryActionLabel}
              </Link>
            )}
          </div>
        </div>

        <nav aria-label="Tipologia di servizio" className="absolute bottom-auto left-0 right-0 top-[403px] grid grid-cols-4 gap-2 lg:right-auto lg:top-[435px] lg:flex lg:gap-3">
          {slides.map((slide, index) => (
            <button
              key={`${slide.tabLabel}-${index}`}
              type="button"
              aria-current={activeIndex === index ? 'page' : undefined}
              onClick={() => {
                setAutoplayActive(false)
                onActiveIndexChange(index)
              }}
              className={`relative min-w-0 rounded-xl border border-white/25 px-1.5 pb-1.5 pt-[14px] text-left text-[9px] font-medium leading-tight backdrop-blur-md transition-colors lg:w-[150px] lg:px-2 lg:pb-2 lg:pt-[18px] lg:text-[13px] ${activeIndex === index ? 'bg-gradient-to-b from-[#0b1b33]/85 to-[#0b1b33]/70 text-white shadow-[0_4px_16px_rgba(11,27,51,0.24)] hover:from-[#0b1b33]/90 hover:to-[#0b1b33]/75' : 'bg-[#0b1b33]/70 text-white hover:bg-[#0b1b33]/80'} aria-[current=page]:text-white`}
            >
              <span className={`absolute left-1.5 right-1.5 top-1.5 block h-[3px] rounded-full lg:left-2 lg:right-2 lg:top-2 lg:h-1 ${activeIndex === index ? 'bg-white' : 'bg-white/35'}`} />
              <span className="block whitespace-normal">{slide.tabLabel}</span>
            </button>
          ))}
        </nav>

        {searchPanel && (
          <div
            data-home-vehicle-search
            onPointerDownCapture={() => setAutoplayActive(false)}
            onClickCapture={() => setAutoplayActive(false)}
            onFocusCapture={() => setAutoplayActive(false)}
            onKeyDownCapture={() => setAutoplayActive(false)}
            className="absolute inset-x-0 top-[452px] z-20 scroll-mt-4 lg:bottom-auto lg:top-[491px]"
          >
            {isValidElement(searchPanel)
              ? cloneElement(searchPanel as ReactElement<{ title?: string; description?: string; searchMode?: string }>, {
                  title: searchTitle,
                  description: searchDescription,
                  searchMode,
                })
              : searchPanel}
          </div>
        )}
      </div>
    </section>
  )
}
